import { useEffect, useRef, useState } from 'react';
import { CircularProgress, makeStyles } from '@material-ui/core';
import { MDSPanel, MDSTypography, MDSButton, MDSPlainButton, MDSIcon, MDSMessageBox } from '@marqvision/mds-v2';
import { downloadFileWithFilename } from '../utils';

const useStyles = makeStyles({
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  panelBody: {
    width: '800px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  contentErrorBox: {
    padding: '0 16px 16px',
  },
  panelContent: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    '& .MuiCircularProgress-root': {
      position: 'absolute',
      top: '50%',
      left: '50%',
    },
    '& iframe': {
      width: '100%',
      height: '100%',
      border: 'none',
    },
  },
});

type Props = {
  report: {
    title: string;
    url: string;
  };

  onClose: () => void;
  onDone?: () => void;
};

// 20초 안에 iframe 내부 파일이 다 로딩되지 않으면 유저에게 현재 상황을 알려주고 reload를 유도한다. - google viewer의 문제!!
const MAX_WAIT_TIME_IN_MS = 20 * 1000;
export const DocumentPreviewPanel = (props: Props) => {
  const classes = useStyles();
  const { report, onClose, onDone } = props;
  const [previewLoadingState, setPreviewLoadingState] = useState({
    url: report.url,
    isLoading: true,
    isError: false,
  });
  const previewUrl = `https://drive.google.com/viewer?url=${report.url}&embedded=true`;

  const timer = useRef<number | null>(null);
  const handleLoaded = () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
    }
    setPreviewLoadingState((prev) => ({
      ...prev,
      isLoading: false,
      isError: false,
    }));
  };

  const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);
  const handleDownloadClick = async () => {
    setIsDownloadInProgress(true);
    await downloadFileWithFilename(report.url, report.title);
    onDone?.();
    setIsDownloadInProgress(false);
  };
  const handleRetryClick = () => {
    setPreviewLoadingState({
      url: previewUrl + '&refresh=' + Date.now(),
      isLoading: true,
      isError: false,
    });
  };

  useEffect(() => {
    setPreviewLoadingState({
      url: previewUrl + '&refresh=' + Date.now(), // 미리보기 전환시 iframe을 강제로 unmount -> mount 시킨다
      isLoading: true,
      isError: false,
    });

    timer.current = window.setTimeout(() => {
      setPreviewLoadingState((prev) => ({
        url: prev.url,
        isLoading: true,
        isError: true,
      }));
    }, MAX_WAIT_TIME_IN_MS);

    return () => {
      if (timer.current) {
        window.clearTimeout(timer.current);
      }
    };
  }, [previewUrl]);

  return (
    <MDSPanel.Wrapper isOpen isDimmed={false} width="800px">
      <MDSPanel.Header onClose={onClose}>
        <div className={classes.panelHeader}>
          <MDSTypography variant="title" size="xl" weight="semibold" lineClamp={1} wordBreak="break-all">
            {report.title}
          </MDSTypography>
          <MDSButton
            variant="border"
            color="bluegray"
            startIcon={<MDSIcon.Download />}
            onClick={handleDownloadClick}
            isDisabled={isDownloadInProgress}
          >
            Download
          </MDSButton>
        </div>
      </MDSPanel.Header>
      <MDSPanel.Content style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {previewLoadingState.isError && (
          <div className={classes.contentErrorBox}>
            <MDSMessageBox
              title="Error loading"
              color="red"
              message="The internet connection is not stable. Please try reloading preview or downloading the file."
              messageCTA={
                <MDSPlainButton size="small" startIcon={<MDSIcon.Refresh />} onClick={handleRetryClick}>
                  Reload
                </MDSPlainButton>
              }
            />
          </div>
        )}
        <div className={classes.panelContent}>
          {previewLoadingState.isLoading && <CircularProgress />}
          <iframe src={previewUrl} onLoad={handleLoaded} />
        </div>
      </MDSPanel.Content>
    </MDSPanel.Wrapper>
  );
};
